import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

const CourseManagement = () => {
  // State for courses
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for active view
  const [activeView, setActiveView] = useState('courses'); // courses, courseForm, quizzes, quizForm, parts, subparts
  
  // State for forms
  const [courseFormData, setCourseFormData] = useState({
    title: '',
    start_date: new Date().toISOString().split('T')[0], // Today's date
    reference_num: 0,
    price: 0,
    description: '',
    categories: [], // Changed to single category
    trailer: '',
    cover_img: '',
    time_commitment: '0-2',
    course_key_points: {}
  });
  
  const [quizFormData, setQuizFormData] = useState({
    title: '',
    instructions: 'Please answer all questions.',
    course_part: 0,
    questions: [
      {
        text: '',
        question_type: 'MCQ',
        options: [
          { text: '', is_correct: false },
          { text: '', is_correct: false },
          { text: '', is_correct: false },
          { text: '', is_correct: false }
        ]
      }
    ]
  });
  
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [courseParts, setCourseParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [subparts, setSubparts] = useState([]);
  const [subpartFormData, setSubpartFormData] = useState({
    subpart_title: '',
    part_description: '',
    course_part: 0,
    video: null
  });
  const [partFormData, setPartFormData] = useState({
    part_title: '',
    part_description: '',
    part_number: 1,
    video: null,
    course: 0
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Loading states for all operations
  const [savingCourse, setSavingCourse] = useState(false);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [savingPart, setSavingPart] = useState(false);
  const [savingSubpart, setSavingSubpart] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);

  // Fetch JWT token from localStorage
  const getJwtToken = () => {
    return localStorage.getItem('jwttoken');
  };

  // API request headers with JWT token
  const getHeaders = () => {
    return {
      'Authorization': `Bearer ${getJwtToken()}`,
      'Content-Type': 'application/json'
    };
  };

  // Show SweetAlert
  const showAlert = (title, text, icon = 'success') => {
    Swal.fire({
      title: title,
      text: text,
      icon: icon,
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
  };

  const showConfirm = (title, text, confirmButtonText = 'Delete') => {
    return Swal.fire({
      title: title,
      text: text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: confirmButtonText,
      cancelButtonText: 'Cancel'
    });
  };

  // Fetch courses on component mount
  useEffect(() => {
    fetchCourses();
    fetchCategories();
  }, []);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await fetch('https://kaasbackend.vetrinahealthcare.com/lms/categories/', {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      
      const data = await response.json();
      // Handle both array of objects and array of strings
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.categories && Array.isArray(data.categories)) {
        setCategories(data.categories);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
      // If categories API fails, initialize with some default categories
      setCategories([
        { category_id: 1, name: 'Technology' },
        { category_id: 2, name: 'Business' },
        { category_id: 3, name: 'Health' },
        { category_id: 4, name: 'Science' },
        { category_id: 5, name: 'Arts' }
      ]);
    }
  };

  // Fetch courses from API
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://kaasbackend.vetrinahealthcare.com/lms/courses/', {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch courses');
      }
      
      const data = await response.json();
      setCourses(data.courses || data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch quizzes
  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://kaasbackend.vetrinahealthcare.com/quiz/quizzes/', {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch quizzes');
      }
      
      const data = await response.json();
      setQuizzes(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch course parts
  const fetchCourseParts = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://kaasbackend.vetrinahealthcare.com/lms/courseparts/', {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch course parts');
      }
      
      const data = await response.json();
      setCourseParts(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Fetch subparts for a specific course part
  const fetchSubparts = async (partId) => {
    try {
      setLoading(true);
      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/subparts/?course_part=${partId}`, {
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch subparts');
      }
      
      const data = await response.json();
      setSubparts(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Create or update a course
  const saveCourse = async (e) => {
    e.preventDefault();
    setSavingCourse(true);
    try {
      const url = isEditing 
        ? `https://kaasbackend.vetrinahealthcare.com/lms/courses/${selectedCourse.course_id}/`
        : 'https://kaasbackend.vetrinahealthcare.com/lms/courses/';
      
      const method = isEditing ? 'PATCH' : 'PUT';
      
      console.log('Saving course with method:', method, 'URL:', url);
      console.log('Course data:', courseFormData);
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(courseFormData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} course: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Course saved successfully:', result);
      
      // Show success message
      showAlert(
        isEditing ? 'Course Updated!' : 'Course Created!',
        isEditing 
          ? `Course "${courseFormData.title}" has been successfully updated.`
          : `Course "${courseFormData.title}" has been successfully created.`,
        'success'
      );
      
      // Reset form and refresh courses
      setCourseFormData({
        title: '',
        start_date: new Date().toISOString().split('T')[0],
        reference_num: 0,
        price: 0,
        description: '',
        categories: [],
        trailer: '',
        cover_img: '',
        time_commitment: '0-2',
        course_key_points: {}
      });
      
      setIsEditing(false);
      setActiveView('courses');
      fetchCourses();
      
    } catch (err) {
      console.error('Error saving course:', err);
      setError(err.message);
      showAlert(
        'Error',
        `Failed to ${isEditing ? 'update' : 'create'} course: ${err.message}`,
        'error'
      );
    }
    finally {
      setSavingCourse(false);
    }
  };

  // Delete a course
  const deleteCourse = async (courseId) => {
    setDeletingItem({ type: 'course', id: courseId });
    try {
      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/courses/${courseId}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete course');
      }
      
      // Show success message
      showAlert('Course Deleted!', 'The course has been successfully deleted.', 'success');
      
      // Refresh courses
      fetchCourses();
      setConfirmDelete(null);
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to delete course: ${err.message}`, 'error');
    }
    finally {
      setDeletingItem(null);
    }
  };

  // Create or update a quiz
  const saveQuiz = async (e) => {
    e.preventDefault();
    setSavingQuiz(true);
    try {
      const url = isEditing 
        ? `https://kaasbackend.vetrinahealthcare.com/quiz/quizzes/${selectedQuiz.id}/`
        : 'https://kaasbackend.vetrinahealthcare.com/quiz/quizzes/';
      
      const method = isEditing ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(quizFormData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} quiz`);
      }
      
      // Show success message
      showAlert(
        isEditing ? 'Quiz Updated!' : 'Quiz Created!',
        isEditing 
          ? `Quiz "${quizFormData.title}" has been successfully updated.`
          : `Quiz "${quizFormData.title}" has been successfully created.`,
        'success'
      );
      
      // Reset form and refresh quizzes
      setQuizFormData({
        title: '',
        instructions: 'Please answer all questions.',
        course_part: 0,
        questions: [
          {
            text: '',
            question_type: 'MCQ',
            options: [
              { text: '', is_correct: false },
              { text: '', is_correct: false },
              { text: '', is_correct: false },
              { text: '', is_correct: false }
            ]
          }
        ]
      });
      
      setIsEditing(false);
      setActiveView('quizzes');
      fetchQuizzes();
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to ${isEditing ? 'update' : 'create'} quiz: ${err.message}`, 'error');
    }
    finally {
      setSavingQuiz(false);
    }
  };

  // Delete a quiz
  const deleteQuiz = async (quizId) => {
    setDeletingItem({ type: 'quiz', id: quizId });
    try {
      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/quiz/quizzes/${quizId}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete quiz');
      }
      
      // Show success message
      showAlert('Quiz Deleted!', 'The quiz has been successfully deleted.', 'success');
      
      // Refresh quizzes
      fetchQuizzes();
      setConfirmDelete(null);
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to delete quiz: ${err.message}`, 'error');
    }
    finally {
      setDeletingItem(null);
    }
  };

  // Create or update a course part
  const savePart = async (e) => {
    e.preventDefault();
    setSavingPart(true);
    try {
      const url = isEditing 
        ? `https://kaasbackend.vetrinahealthcare.com/lms/courseparts/${selectedPart.part_id}/`
        : 'https://kaasbackend.vetrinahealthcare.com/lms/courseparts/';
      
      const method = isEditing ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(partFormData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} course part`);
      }
      
      // Show success message
      showAlert(
        isEditing ? 'Course Part Updated!' : 'Course Part Created!',
        isEditing 
          ? `Course part "${partFormData.part_title}" has been successfully updated.`
          : `Course part "${partFormData.part_title}" has been successfully created.`,
        'success'
      );
      
      // Reset form and refresh parts
      setPartFormData({
        part_title: '',
        part_description: '',
        part_number: 1,
        video: null,
        course: 0
      });
      
      setIsEditing(false);
      setActiveView('parts');
      fetchCourseParts();
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to ${isEditing ? 'update' : 'create'} course part: ${err.message}`, 'error');
    }
    finally {
      setSavingPart(false);
    }
  };

  // Delete a course part
  const deletePart = async (partId) => {
    setDeletingItem({ type: 'part', id: partId });
    try {
      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/courseparts/${partId}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete course part');
      }
      
      // Show success message
      showAlert('Course Part Deleted!', 'The course part has been successfully deleted.', 'success');
      
      // Refresh course parts
      fetchCourseParts();
      setConfirmDelete(null);
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to delete course part: ${err.message}`, 'error');
    }
    finally {
      setDeletingItem(null);
    }
  };

  // Create or update a subpart
  const saveSubpart = async (e) => {
    e.preventDefault();
    setSavingSubpart(true);
    try {
      const url = isEditing 
        ? `https://kaasbackend.vetrinahealthcare.com/lms/subparts/${selectedPart.subpart_id}/`
        : 'https://kaasbackend.vetrinahealthcare.com/lms/subparts/';
      
      const method = isEditing ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(subpartFormData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} subpart`);
      }
      
      // Show success message
      showAlert(
        isEditing ? 'Subpart Updated!' : 'Subpart Created!',
        isEditing 
          ? `Subpart "${subpartFormData.subpart_title}" has been successfully updated.`
          : `Subpart "${subpartFormData.subpart_title}" has been successfully created.`,
        'success'
      );
      
      // Reset form and refresh subparts
      setSubpartFormData({
        subpart_title: '',
        part_description: '',
        course_part: 0,
        video: null
      });
      
      setIsEditing(false);
      setActiveView('subparts');
      fetchSubparts(selectedPart.course_part);
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to ${isEditing ? 'update' : 'create'} subpart: ${err.message}`, 'error');
    }
    finally {
      setSavingSubpart(false);
    }
  };

  // Delete a subpart
  const deleteSubpart = async (subpartId) => {
    setDeletingItem({ type: 'subpart', id: subpartId });
    try {
      const response = await fetch(`https://kaasbackend.vetrinahealthcare.com/lms/subparts/${subpartId}/`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete subpart');
      }
      
      // Show success message
      showAlert('Subpart Deleted!', 'The subpart has been successfully deleted.', 'success');
      
      // Refresh subparts
      fetchSubparts(selectedPart.course_part);
      setConfirmDelete(null);
      
    } catch (err) {
      setError(err.message);
      showAlert('Error', `Failed to delete subpart: ${err.message}`, 'error');
    }
    finally {
      setDeletingItem(null);
    }
  };

  // Handle course form input changes
  const handleCourseInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cover_img') {
      // Handle file upload
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setCourseFormData({
            ...courseFormData,
            cover_img: reader.result
          });
        };
        reader.readAsDataURL(file);
      }
    } else if (name === 'trailer') {
      // Handle trailer file upload - only MP4 files
      const file = e.target.files[0];
      if (file) {
        // Check if file is MP4
        if (file.type !== 'video/mp4') {
          setError('Please select an MP4 video file only.');
          return;
        }
        
        const reader = new FileReader();
        reader.onloadend = () => {
          setCourseFormData({
            ...courseFormData,
            trailer: reader.result
          });
        };
        reader.readAsDataURL(file);
      }
    } else {
      setCourseFormData({
        ...courseFormData,
        [name]: value
      });
    }
  };

  // Handle category selection - SINGLE SELECTION
  const handleCategorySelect = (category) => {
    // Extract category name whether it's an object or string
    const categoryName = typeof category === 'object' ? category.name : category;
    
    setCourseFormData({
      ...courseFormData,
      categories: [categoryName] // Single selection - replace array with single item
    });
    setShowCategoryDropdown(false);
  };

  // Remove category
  const removeCategory = () => {
    setCourseFormData({
      ...courseFormData,
      categories: [] // Clear the category
    });
  };

  // Handle trailer file selection
  const handleTrailerFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is MP4
      if (file.type !== 'video/mp4') {
        setError('Please select an MP4 video file only.');
        e.target.value = ''; // Reset file input
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setCourseFormData({
          ...courseFormData,
          trailer: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle quiz form input changes
  const handleQuizInputChange = (e, questionIndex = null, optionIndex = null) => {
    const { name, value, type, checked } = e.target;
    
    if (questionIndex === null) {
      // Main quiz properties
      setQuizFormData({
        ...quizFormData,
        [name]: value
      });
    } else if (optionIndex === null) {
      // Question properties
      const updatedQuestions = [...quizFormData.questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        [name]: value
      };
      
      setQuizFormData({
        ...quizFormData,
        questions: updatedQuestions
      });
    } else {
      // Option properties
      const updatedQuestions = [...quizFormData.questions];
      
      if (name === 'is_correct') {
        // For radio buttons, set all options to false first
        if (updatedQuestions[questionIndex].question_type === 'MCQ') {
          updatedQuestions[questionIndex].options = updatedQuestions[questionIndex].options.map((opt, idx) => ({
            ...opt,
            is_correct: idx === optionIndex
          }));
        } else {
          // For TF questions
          updatedQuestions[questionIndex].options[optionIndex] = {
            ...updatedQuestions[questionIndex].options[optionIndex],
            is_correct: checked
          };
        }
      } else {
        updatedQuestions[questionIndex].options[optionIndex] = {
          ...updatedQuestions[questionIndex].options[optionIndex],
          [name]: value
        };
      }
      
      setQuizFormData({
        ...quizFormData,
        questions: updatedQuestions
      });
    }
  };

  // Add a new question to the quiz
  const addQuestion = () => {
    setQuizFormData({
      ...quizFormData,
      questions: [
        ...quizFormData.questions,
        {
          text: '',
          question_type: 'MCQ',
          options: [
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false }
          ]
        }
      ]
    });
  };

  // Remove a question from the quiz
  const removeQuestion = (index) => {
    const updatedQuestions = [...quizFormData.questions];
    updatedQuestions.splice(index, 1);
    
    setQuizFormData({
      ...quizFormData,
      questions: updatedQuestions
    });
  };

  // Change question type
  const changeQuestionType = (index, type) => {
    const updatedQuestions = [...quizFormData.questions];
    
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      question_type: type,
      options: type === 'TF' 
        ? [
            { text: 'True', is_correct: false },
            { text: 'False', is_correct: false }
          ]
        : [
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false }
          ]
    };
    
    setQuizFormData({
      ...quizFormData,
      questions: updatedQuestions
    });
  };

  // Edit a course
  const editCourse = (course) => {
    setCourseFormData({
      title: course.title || '',
      start_date: course.start_date || new Date().toISOString().split('T')[0],
      reference_num: course.reference_num || 0,
      price: course.price || 0,
      description: course.description || '',
      categories: Array.isArray(course.categories) ? course.categories : [course.categories].filter(Boolean), // Handle both array and single value
      trailer: course.trailer || '',
      cover_img: course.cover_img || '',
      time_commitment: course.time_commitment || '0-2',
      course_key_points: course.course_key_points || {}
    });
    
    setSelectedCourse(course);
    setIsEditing(true);
    setActiveView('courseForm');
  };

  // Edit a quiz
  const editQuiz = (quiz) => {
    setQuizFormData({
      title: quiz.title || '',
      instructions: quiz.instructions || 'Please answer all questions.',
      course_part: quiz.course_part || 0,
      questions: quiz.questions || [
        {
          text: '',
          question_type: 'MCQ',
          options: [
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false },
            { text: '', is_correct: false }
          ]
        }
      ]
    });
    
    setSelectedQuiz(quiz);
    setIsEditing(true);
    setActiveView('quizForm');
  };

  // Filter courses based on search term
  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Loading styles
  const loadingStyles = {
    buttonLoading: {
      position: 'relative',
      color: 'transparent',
    },
    spinner: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '16px',
      height: '16px',
      border: '2px solid transparent',
      borderTop: '2px solid #fff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }
  };

  // Add CSS animation for spinner
  const spinnerStyle = `
    @keyframes spin {
      0% { transform: translate(-50%, -50%) rotate(0deg); }
      100% { transform: translate(-50%, -50%) rotate(360deg); }
    }
  `;

  // Styles
  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      padding: '10px 0',
      borderBottom: '1px solid #eee',
    },
    title: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#333',
      margin: 0,
    },
    nav: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
    },
    navButton: {
      padding: '8px 16px',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: 'bold',
      color: '#333',
    },
    activeNavButton: {
      backgroundColor: '#4a90e2',
      color: 'white',
    },
    searchContainer: {
      marginBottom: '20px',
      display: 'flex',
      gap: '10px',
    },
    searchInput: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      flexGrow: 1,
    },
    button: {
      padding: '8px 16px',
      backgroundColor: '#4a90e2',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: 'bold',
      position: 'relative',
    },
    fileInputButton: {
      padding: '8px 16px',
      backgroundColor: '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: 'bold',
      display: 'inline-block',
      textAlign: 'center',
    },
    dangerButton: {
      backgroundColor: '#e74c3c',
    },
    successButton: {
      backgroundColor: '#2ecc71',
    },
    card: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '15px',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    courseGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px',
    },
    courseCard: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    courseImageContainer: {
      width: '100%',
      height: '180px',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#f0f0f0',
    },
    courseImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      position: 'absolute',
      top: 0,
      left: 0,
    },
    noImagePlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f0f0f0',
      color: '#999'
    },
    courseContent: {
      padding: '15px',
    },
    courseTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '10px',
      color: '#333',
    },
    courseDescription: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '10px',
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    courseDetails: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '10px',
      fontSize: '14px',
      color: '#666',
    },
    coursePrice: {
      fontWeight: 'bold',
      color: '#4a90e2',
    },
    courseActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '10px',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      position: 'relative',
    },
    label: {
      fontWeight: 'bold',
      fontSize: '14px',
      color: '#333',
    },
    input: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
    },
    textarea: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      width: '100%',
    },
    fileInput: {
      display: 'none',
    },
    formActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '20px',
    },
    questionCard: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '15px',
      backgroundColor: '#f9f9f9',
    },
    optionGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '10px',
    },
    modalBackdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      width: '400px',
      maxWidth: '90%',
    },
    modalTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '15px',
      color: '#333',
    },
    modalText: {
      marginBottom: '20px',
      color: '#666',
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
    },
    error: {
      color: '#e74c3c',
      padding: '10px',
      backgroundColor: '#fadbd8',
      borderRadius: '4px',
      marginBottom: '15px',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#666',
    },
    badge: {
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      backgroundColor: '#e0e0e0',
      color: '#333',
    },
    priceBadge: {
      backgroundColor: '#e1f5fe',
      color: '#0288d1',
    },
    categoryBadge: {
      backgroundColor: '#e8f5e9',
      color: '#388e3c',
      marginRight: '5px',
      marginBottom: '5px',
      display: 'inline-flex',
      alignItems: 'center',
    },
    removeCategory: {
      marginLeft: '5px',
      cursor: 'pointer',
      fontWeight: 'bold',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '20px',
    },
    th: {
      textAlign: 'left',
      padding: '12px 15px',
      backgroundColor: '#f8f9fa',
      borderBottom: '2px solid #ddd',
      color: '#333',
      fontWeight: 'bold',
    },
    td: {
      padding: '10px 15px',
      borderBottom: '1px solid #ddd',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: '#666',
    },
    emptyStateIcon: {
      fontSize: '48px',
      marginBottom: '10px',
      color: '#ddd',
    },
    emptyStateText: {
      fontSize: '16px',
      marginBottom: '20px',
    },
    fileInfo: {
      fontSize: '14px',
      color: '#666',
      marginTop: '5px',
      fontStyle: 'italic',
    },
    trailerPreview: {
      marginTop: '10px',
      maxWidth: '300px',
    },
    categoryDropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 10,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    categoryItem: {
      padding: '8px 12px',
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0',
    },
    selectedCategories: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      marginTop: '10px',
    },
    dropdownContainer: {
      position: 'relative',
      width: '100%',
    },
    dropdownHeader: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dropdownList: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #ddd',
      borderRadius: '4px',
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 10,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    dropdownItem: {
      padding: '8px 12px',
      cursor: 'pointer',
      borderBottom: '1px solid #f0f0f0',
    }
  };

  return (
    <div style={styles.container}>
      <style>{spinnerStyle}</style>
      
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Course Management System</h1>
      </div>
      
      {/* Navigation */}
      <div style={styles.nav}>
        <button 
          style={{
            ...styles.navButton,
            ...(activeView === 'courses' ? styles.activeNavButton : {})
          }}
          onClick={() => {
            setActiveView('courses');
            fetchCourses();
          }}
        >
          Courses
        </button>
        <button 
          style={{
            ...styles.navButton,
            ...(activeView === 'quizzes' ? styles.activeNavButton : {})
          }}
          onClick={() => {
            setActiveView('quizzes');
            fetchQuizzes();
          }}
        >
          Quizzes
        </button>
        <button 
          style={{
            ...styles.navButton,
            ...(activeView === 'parts' ? styles.activeNavButton : {})
          }}
          onClick={() => {
            setActiveView('parts');
            fetchCourseParts();
          }}
        >
          Course Parts
        </button>
      </div>
      
      {/* Error message */}
      {error && (
        <div style={styles.error}>
          <p>{error}</p>
          <button 
            style={{...styles.button, marginTop: '10px'}}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}
      
      {/* Loading indicator */}
      {loading && (
        <div style={styles.loading}>
          <p>Loading...</p>
        </div>
      )}
      
      {/* Courses View */}
      {activeView === 'courses' && !loading && (
        <>
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button 
              style={styles.button}
              onClick={() => {
                setIsEditing(false);
                setCourseFormData({
                  title: '',
                  start_date: new Date().toISOString().split('T')[0],
                  reference_num: 0,
                  price: 0,
                  description: '',
                  categories: [],
                  trailer: '',
                  cover_img: '',
                  time_commitment: '0-2',
                  course_key_points: {}
                });
                setActiveView('courseForm');
              }}
            >
              Add New Course
            </button>
          </div>
          
          {filteredCourses.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📚</div>
              <p style={styles.emptyStateText}>No courses found. Create your first course!</p>
              <button 
                style={styles.button}
                onClick={() => {
                  setIsEditing(false);
                  setCourseFormData({
                    title: '',
                    start_date: new Date().toISOString().split('T')[0],
                    reference_num: 0,
                    price: 0,
                    description: '',
                    categories: [],
                    trailer: '',
                    cover_img: '',
                    time_commitment: '0-2',
                    course_key_points: {}
                  });
                  setActiveView('courseForm');
                }}
              >
                Create New Course
              </button>
            </div>
          ) : (
            <div style={styles.courseGrid}>
              {filteredCourses.map(course => (
                <div key={course.course_id} style={styles.courseCard}>
                  <div style={styles.courseImageContainer}>
                    {course.cover_img ? (
                      <img 
                        src={course.cover_img} 
                        alt={course.title} 
                        style={styles.courseImage} 
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div style={{
                      ...styles.noImagePlaceholder,
                      display: course.cover_img ? 'none' : 'flex'
                    }}>
                      No Image
                    </div>
                  </div>
                  <div style={styles.courseContent}>
                    <h3 style={styles.courseTitle}>{course.title}</h3>
                    <p style={styles.courseDescription}>
                      {course.description || 'No description available'}
                    </p>
                    <div>
                      {course.categories && course.categories.map((category, index) => (
                        <span key={index} style={{...styles.badge, ...styles.categoryBadge}}>
                          {category}
                        </span>
                      ))}
                    </div>
                    <div style={styles.courseDetails}>
                      <span>
                        <strong>Start Date:</strong> {course.start_date || 'Not set'}
                      </span>
                      <span style={styles.coursePrice}>
                        ${course.price || 0}
                      </span>
                    </div>
                    <div style={styles.courseActions}>
                      <button 
                        style={styles.button}
                        onClick={() => editCourse(course)}
                      >
                        Edit
                      </button>
                      <button 
                        style={{...styles.button, ...styles.dangerButton}}
                        onClick={() => setConfirmDelete({ type: 'course', id: course.course_id })}
                        disabled={deletingItem && deletingItem.type === 'course' && deletingItem.id === course.course_id}
                      >
                        {deletingItem && deletingItem.type === 'course' && deletingItem.id === course.course_id ? (
                          <div style={loadingStyles.buttonLoading}>
                            Deleting...
                            <div style={loadingStyles.spinner}></div>
                          </div>
                        ) : (
                          'Delete'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      
      {/* Course Form View */}
      {activeView === 'courseForm' && (
        <div style={styles.card}>
          <h2>{isEditing ? 'Edit Course' : 'Create New Course'}</h2>
          <form style={styles.form} onSubmit={saveCourse}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                name="title"
                value={courseFormData.title}
                onChange={handleCourseInputChange}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                name="start_date"
                value={courseFormData.start_date}
                onChange={handleCourseInputChange}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Reference Number</label>
              <input
                type="number"
                name="reference_num"
                value={courseFormData.reference_num}
                onChange={handleCourseInputChange}
                style={styles.input}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Price</label>
              <input
                type="number"
                name="price"
                value={courseFormData.price}
                onChange={handleCourseInputChange}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                name="description"
                value={courseFormData.description}
                onChange={handleCourseInputChange}
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Category</label>
              <div style={styles.dropdownContainer}>
                <div 
                  style={styles.dropdownHeader}
                  onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                >
                  <span>
                    {courseFormData.categories.length > 0 ? courseFormData.categories[0] : 'Select a category'}
                  </span>
                  <span>▼</span>
                </div>
                {showCategoryDropdown && (
                  <div style={styles.dropdownList}>
                    {categories.map((category, index) => (
                      <div
                        key={index}
                        style={{
                          ...styles.dropdownItem,
                          backgroundColor: courseFormData.categories.includes(typeof category === 'object' ? category.name : category) ? '#e0e0e0' : 'white'
                        }}
                        onClick={() => handleCategorySelect(category)}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = courseFormData.categories.includes(typeof category === 'object' ? category.name : category) ? '#e0e0e0' : 'white'}
                      >
                        {typeof category === 'object' ? category.name : category}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Selected Category */}
              {courseFormData.categories.length > 0 && (
                <div style={styles.selectedCategories}>
                  <span style={{...styles.badge, ...styles.categoryBadge}}>
                    {courseFormData.categories[0]}
                    <span 
                      style={styles.removeCategory}
                      onClick={removeCategory}
                    >
                      ×
                    </span>
                  </span>
                </div>
              )}
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Course Trailer (MP4 only)</label>
              <input
                type="file"
                name="trailer"
                onChange={handleCourseInputChange}
                style={styles.input}
                accept="video/mp4"
              />
              {courseFormData.trailer && (
                <div style={{ marginTop: "10px" }}>
                  <video
                    src={courseFormData.trailer}
                    controls
                    style={{ maxWidth: "300px", maxHeight: "200px", borderRadius: "8px" }}
                  />
                </div>
              )}
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Cover Image</label>
              <input
                type="file"
                name="cover_img"
                onChange={handleCourseInputChange}
                style={styles.input}
                accept="image/*"
              />
              {courseFormData.cover_img && (
                <div style={{ marginTop: '10px' }}>
                  <img 
                    src={courseFormData.cover_img} 
                    alt="Cover preview" 
                    style={{ maxWidth: '200px', maxHeight: '150px' }} 
                  />
                </div>
              )}
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Time Commitment</label>
              <select
                name="time_commitment"
                value={courseFormData.time_commitment}
                onChange={handleCourseInputChange}
                style={styles.select}
              >
                <option value="0-2">0-2 hours/week</option>
                <option value="2-4">2-4 hours/week</option>
                <option value="4-6">4-6 hours/week</option>
                <option value="6-8">6-8 hours/week</option>
                <option value="8+">8+ hours/week</option>
              </select>
            </div>
            
            <div style={styles.formActions}>
              <button
                type="button"
                style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
                onClick={() => setActiveView('courses')}
                disabled={savingCourse}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={styles.button}
                disabled={savingCourse}
              >
                {savingCourse ? (
                  <div style={loadingStyles.buttonLoading}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                    <div style={loadingStyles.spinner}></div>
                  </div>
                ) : (
                  isEditing ? 'Update Course' : 'Create Course'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Quizzes View */}
      {activeView === 'quizzes' && !loading && (
        <>
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search quizzes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button 
              style={styles.button}
              onClick={() => {
                setIsEditing(false);
                setQuizFormData({
                  title: '',
                  instructions: 'Please answer all questions.',
                  course_part: 0,
                  questions: [
                    {
                      text: '',
                      question_type: 'MCQ',
                      options: [
                        { text: '', is_correct: false },
                        { text: '', is_correct: false },
                        { text: '', is_correct: false },
                        { text: '', is_correct: false }
                      ]
                    }
                  ]
                });
                setActiveView('quizForm');
              }}
            >
              Add New Quiz
            </button>
          </div>
          
          {quizzes.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📝</div>
              <p style={styles.emptyStateText}>No quizzes found. Create your first quiz!</p>
              <button 
                style={styles.button}
                onClick={() => {
                  setIsEditing(false);
                  setQuizFormData({
                    title: '',
                    instructions: 'Please answer all questions.',
                    course_part: 0,
                    questions: [
                      {
                        text: '',
                        question_type: 'MCQ',
                        options: [
                          { text: '', is_correct: false },
                          { text: '', is_correct: false },
                          { text: '', is_correct: false },
                          { text: '', is_correct: false }
                        ]
                      }
                    ]
                  });
                  setActiveView('quizForm');
                }}
              >
                Create New Quiz
              </button>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Course Part</th>
                  <th style={styles.th}>Questions</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.filter(quiz => 
                  quiz.title.toLowerCase().includes(searchTerm.toLowerCase())
                ).map(quiz => (
                  <tr key={quiz.id}>
                    <td style={styles.td}>{quiz.title}</td>
                    <td style={styles.td}>{quiz.course_part}</td>
                    <td style={styles.td}>{quiz.questions?.length || 0} questions</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          style={styles.button}
                          onClick={() => editQuiz(quiz)}
                        >
                          Edit
                        </button>
                        <button 
                          style={{...styles.button, ...styles.dangerButton}}
                          onClick={() => setConfirmDelete({ type: 'quiz', id: quiz.id })}
                          disabled={deletingItem && deletingItem.type === 'quiz' && deletingItem.id === quiz.id}
                        >
                          {deletingItem && deletingItem.type === 'quiz' && deletingItem.id === quiz.id ? (
                            <div style={loadingStyles.buttonLoading}>
                              Deleting...
                              <div style={loadingStyles.spinner}></div>
                            </div>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      
      {/* Quiz Form View */}
      {activeView === 'quizForm' && (
        <div style={styles.card}>
          <h2>{isEditing ? 'Edit Quiz' : 'Create New Quiz'}</h2>
          <form style={styles.form} onSubmit={saveQuiz}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                name="title"
                value={quizFormData.title}
                onChange={(e) => handleQuizInputChange(e)}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Instructions</label>
              <textarea
                name="instructions"
                value={quizFormData.instructions}
                onChange={(e) => handleQuizInputChange(e)}
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Course Part ID</label>
              <input
                type="number"
                name="course_part"
                value={quizFormData.course_part}
                onChange={(e) => handleQuizInputChange(e)}
                style={styles.input}
                required
              />
            </div>
            
            <h3 style={{ marginTop: '20px', marginBottom: '10px' }}>Questions</h3>
            
            {quizFormData.questions.map((question, questionIndex) => (
              <div key={questionIndex} style={styles.questionCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0 }}>Question {questionIndex + 1}</h4>
                  <button
                    type="button"
                    style={{...styles.button, ...styles.dangerButton, padding: '4px 8px'}}
                    onClick={() => removeQuestion(questionIndex)}
                  >
                    Remove
                  </button>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Question Text</label>
                  <input
                    type="text"
                    name="text"
                    value={question.text}
                    onChange={(e) => handleQuizInputChange(e, questionIndex)}
                    style={styles.input}
                    required
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Question Type</label>
                  <select
                    name="question_type"
                    value={question.question_type}
                    onChange={(e) => changeQuestionType(questionIndex, e.target.value)}
                    style={styles.select}
                  >
                    <option value="MCQ">Multiple Choice</option>
                    <option value="TF">True/False</option>
                  </select>
                </div>
                
                <div style={{ marginTop: '10px' }}>
                  <label style={styles.label}>Options</label>
                  {question.options.map((option, optionIndex) => (
                    <div key={optionIndex} style={styles.optionGroup}>
                      <input
                        type={question.question_type === 'MCQ' ? 'radio' : 'checkbox'}
                        name={`is_correct_${questionIndex}`}
                        checked={option.is_correct}
                        onChange={(e) => handleQuizInputChange(
                          { target: { name: 'is_correct', checked: e.target.checked } },
                          questionIndex,
                          optionIndex
                        )}
                      />
                      <input
                        type="text"
                        name="text"
                        value={option.text}
                        onChange={(e) => handleQuizInputChange(e, questionIndex, optionIndex)}
                        style={{ ...styles.input, flexGrow: 1 }}
                        placeholder="Option text"
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              type="button"
              style={{...styles.button, marginTop: '10px'}}
              onClick={addQuestion}
            >
              Add Question
            </button>
            
            <div style={styles.formActions}>
              <button
                type="button"
                style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
                onClick={() => setActiveView('quizzes')}
                disabled={savingQuiz}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={styles.button}
                disabled={savingQuiz}
              >
                {savingQuiz ? (
                  <div style={loadingStyles.buttonLoading}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                    <div style={loadingStyles.spinner}></div>
                  </div>
                ) : (
                  isEditing ? 'Update Quiz' : 'Create Quiz'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Course Parts View */}
      {activeView === 'parts' && !loading && (
        <>
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search course parts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button 
              style={styles.button}
              onClick={() => {
                setIsEditing(false);
                setPartFormData({
                  part_title: '',
                  part_description: '',
                  part_number: 1,
                  video: null,
                  course: 0
                });
                setActiveView('partForm');
              }}
            >
              Add New Course Part
            </button>
          </div>
          
          {courseParts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📚</div>
              <p style={styles.emptyStateText}>No course parts found. Create your first course part!</p>
              <button 
                style={styles.button}
                onClick={() => {
                  setIsEditing(false);
                  setPartFormData({
                    part_title: '',
                    part_description: '',
                    part_number: 1,
                    video: null,
                    course: 0
                  });
                  setActiveView('partForm');
                }}
              >
                Create New Course Part
              </button>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Course ID</th>
                  <th style={styles.th}>Part Number</th>
                  <th style={styles.th}>Subparts</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {courseParts.filter(part => 
                  part.part_title.toLowerCase().includes(searchTerm.toLowerCase())
                ).map(part => (
                  <tr key={part.part_id}>
                    <td style={styles.td}>{part.part_title}</td>
                    <td style={styles.td}>{part.course}</td>
                    <td style={styles.td}>{part.part_number}</td>
                    <td style={styles.td}>{part.subparts?.length || 0} subparts</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          style={styles.button}
                          onClick={() => {
                            setSelectedPart(part);
                            fetchSubparts(part.part_id);
                            setActiveView('subparts');
                          }}
                        >
                          View Subparts
                        </button>
                        <button 
                          style={styles.button}
                          onClick={() => {
                            setPartFormData({
                              part_title: part.part_title || '',
                              part_description: part.part_description || '',
                              part_number: part.part_number || 1,
                              video: part.video || null,
                              course: part.course || 0
                            });
                            setSelectedPart(part);
                            setIsEditing(true);
                            setActiveView('partForm');
                          }}
                        >
                          Edit
                        </button>
                        <button 
                          style={{...styles.button, ...styles.dangerButton}}
                          onClick={() => setConfirmDelete({ type: 'part', id: part.part_id })}
                          disabled={deletingItem && deletingItem.type === 'part' && deletingItem.id === part.part_id}
                        >
                          {deletingItem && deletingItem.type === 'part' && deletingItem.id === part.part_id ? (
                            <div style={loadingStyles.buttonLoading}>
                              Deleting...
                              <div style={loadingStyles.spinner}></div>
                            </div>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      
      {/* Subparts View */}
      {activeView === 'subparts' && !loading && selectedPart && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <button
              style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
              onClick={() => setActiveView('parts')}
            >
              Back to Course Parts
            </button>
          </div>
          
          <h2>Subparts for: {selectedPart.part_title}</h2>
          
          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search subparts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button 
              style={styles.button}
              onClick={() => {
                setIsEditing(false);
                setSubpartFormData({
                  subpart_title: '',
                  part_description: '',
                  course_part: selectedPart.part_id,
                  video: null
                });
                setActiveView('subpartForm');
              }}
            >
              Add New Subpart
            </button>
          </div>
          
          {subparts.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyStateIcon}>📄</div>
              <p style={styles.emptyStateText}>No subparts found. Create your first subpart!</p>
              <button 
                style={styles.button}
                onClick={() => {
                  setIsEditing(false);
                  setSubpartFormData({
                    subpart_title: '',
                    part_description: '',
                    course_part: selectedPart.part_id,
                    video: null
                  });
                  setActiveView('subpartForm');
                }}
              >
                Create New Subpart
              </button>
            </div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subparts.filter(subpart => 
                  subpart.subpart_title.toLowerCase().includes(searchTerm.toLowerCase())
                ).map(subpart => (
                  <tr key={subpart.subpart_id}>
                    <td style={styles.td}>{subpart.subpart_title}</td>
                    <td style={styles.td}>{subpart.part_description || 'No description'}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: subpart.is_complete ? '#e8f5e9' : '#fff8e6',
                        color: subpart.is_complete ? '#388e3c' : '#ff9800'
                      }}>
                        {subpart.is_complete ? 'Complete' : 'Incomplete'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                          style={styles.button}
                          onClick={() => {
                            setSubpartFormData({
                              subpart_title: subpart.subpart_title || '',
                              part_description: subpart.part_description || '',
                              course_part: subpart.course_part || selectedPart.part_id,
                              video: subpart.video || null
                            });
                            setSelectedPart(subpart);
                            setIsEditing(true);
                            setActiveView('subpartForm');
                          }}
                        >
                          Edit
                        </button>
                        <button 
                          style={{...styles.button, ...styles.dangerButton}}
                          onClick={() => setConfirmDelete({ type: 'subpart', id: subpart.subpart_id })}
                          disabled={deletingItem && deletingItem.type === 'subpart' && deletingItem.id === subpart.subpart_id}
                        >
                          {deletingItem && deletingItem.type === 'subpart' && deletingItem.id === subpart.subpart_id ? (
                            <div style={loadingStyles.buttonLoading}>
                              Deleting...
                              <div style={loadingStyles.spinner}></div>
                            </div>
                          ) : (
                            'Delete'
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      
      {/* Part Form View */}
      {activeView === 'partForm' && (
        <div style={styles.card}>
          <h2>{isEditing ? 'Edit Course Part' : 'Create New Course Part'}</h2>
          <form style={styles.form} onSubmit={savePart}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                name="part_title"
                value={partFormData.part_title}
                onChange={(e) => setPartFormData({...partFormData, part_title: e.target.value})}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                name="part_description"
                value={partFormData.part_description}
                onChange={(e) => setPartFormData({...partFormData, part_description: e.target.value})}
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Part Number</label>
              <input
                type="number"
                name="part_number"
                value={partFormData.part_number}
                onChange={(e) => setPartFormData({...partFormData, part_number: parseInt(e.target.value)})}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Course ID</label>
              <input
                type="number"
                name="course"
                value={partFormData.course}
                onChange={(e) => setPartFormData({...partFormData, course: parseInt(e.target.value)})}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Video</label>
              <input
                type="file"
                name="video"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setPartFormData({...partFormData, video: reader.result});
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={styles.input}
                accept="video/*"
              />
            </div>
            
            <div style={styles.formActions}>
              <button
                type="button"
                style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
                onClick={() => setActiveView('parts')}
                disabled={savingPart}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={styles.button}
                disabled={savingPart}
              >
                {savingPart ? (
                  <div style={loadingStyles.buttonLoading}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                    <div style={loadingStyles.spinner}></div>
                  </div>
                ) : (
                  isEditing ? 'Update Course Part' : 'Create Course Part'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Subpart Form View */}
      {activeView === 'subpartForm' && (
        <div style={styles.card}>
          <h2>{isEditing ? 'Edit Subpart' : 'Create New Subpart'}</h2>
          <form style={styles.form} onSubmit={saveSubpart}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Title</label>
              <input
                type="text"
                name="subpart_title"
                value={subpartFormData.subpart_title}
                onChange={(e) => setSubpartFormData({...subpartFormData, subpart_title: e.target.value})}
                style={styles.input}
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <textarea
                name="part_description"
                value={subpartFormData.part_description}
                onChange={(e) => setSubpartFormData({...subpartFormData, part_description: e.target.value})}
                style={styles.textarea}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Course Part ID</label>
              <input
                type="number"
                name="course_part"
                value={subpartFormData.course_part}
                onChange={(e) => setSubpartFormData({...subpartFormData, course_part: parseInt(e.target.value)})}
                style={styles.input}
                required
                readOnly={selectedPart && selectedPart.part_id}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Video</label>
              <input
                type="file"
                name="video"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setSubpartFormData({...subpartFormData, video: reader.result});
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={styles.input}
                accept="video/*"
              />
            </div>
            
            <div style={styles.formActions}>
              <button
                type="button"
                style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
                onClick={() => setActiveView('subparts')}
                disabled={savingSubpart}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={styles.button}
                disabled={savingSubpart}
              >
                {savingSubpart ? (
                  <div style={loadingStyles.buttonLoading}>
                    {isEditing ? 'Updating...' : 'Creating...'}
                    <div style={loadingStyles.spinner}></div>
                  </div>
                ) : (
                  isEditing ? 'Update Subpart' : 'Create Subpart'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {confirmDelete && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Confirm Delete</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete this {confirmDelete.type}? This action cannot be undone.
            </p>
            <div style={styles.modalActions}>
              <button
                style={{...styles.button, backgroundColor: '#ccc', color: '#333'}}
                onClick={() => setConfirmDelete(null)}
                disabled={deletingItem}
              >
                Cancel
              </button>
              <button
                style={{...styles.button, ...styles.dangerButton}}
                onClick={() => {
                  if (confirmDelete.type === 'course') {
                    deleteCourse(confirmDelete.id);
                  } else if (confirmDelete.type === 'quiz') {
                    deleteQuiz(confirmDelete.id);
                  } else if (confirmDelete.type === 'part') {
                    deletePart(confirmDelete.id);
                  } else if (confirmDelete.type === 'subpart') {
                    deleteSubpart(confirmDelete.id);
                  }
                }}
                disabled={deletingItem}
              >
                {deletingItem ? (
                  <div style={loadingStyles.buttonLoading}>
                    Deleting...
                    <div style={loadingStyles.spinner}></div>
                  </div>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseManagement;